/**
 * Menu - Start screen with game title and a Play button.
 */

import * as BABYLON from "babylonjs";
import * as GUI from "babylonjs-gui";
import { DifficultyLevel } from "../systems/DifficultyManager";

export class Menu {
  private _texture: GUI.AdvancedDynamicTexture;
  private _container: GUI.Rectangle;
  private _videoElement?: HTMLVideoElement;
  private _selectedDifficulty: DifficultyLevel = DifficultyLevel.EASY;
  private _easyBtn?: GUI.Rectangle;
  private _mediumBtn?: GUI.Rectangle;
  private _hardBtn?: GUI.Rectangle;
  private _impossibleBtn?: GUI.Rectangle;
  private _difficultyDesc?: GUI.TextBlock;
  private _onPlayWithDifficulty?: (difficulty: DifficultyLevel) => void;

  constructor(
    scene: BABYLON.Scene,
    onPractice: () => void,
    onPlay: () => void,
    onCrowdToggle: (show: boolean) => void,
    onPlayWithDifficulty?: (difficulty: DifficultyLevel) => void
  ) {
    this._onPlayWithDifficulty = onPlayWithDifficulty;
    this._texture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("menu");

    // Debug: log all pointer events on the texture
    this._texture.onControlPickedObservable.add((control) => {
      console.log("GUI Control picked:", control?.name);
    });

    // Full-screen overlay - allow pointer events to pass through to children
    this._container = new GUI.Rectangle("menuBg");
    this._container.width = "100%";
    this._container.height = "100%";
    this._container.thickness = 0;
    this._container.background = "rgba(0,0,0,0.7)";
    this._container.isHitTestVisible = false;
    this._container.isPointerBlocker = false;
    this._texture.addControl(this._container);

    // Add Video Background Container (Placeholder in GUI)
    const videoContainer = new GUI.Rectangle("videoContainer");
    videoContainer.width = "450px";
    videoContainer.height = "450px"; // 1:1 ratio
    videoContainer.cornerRadius = 20;
    videoContainer.thickness = 8;
    videoContainer.color = "#1155cc";
    videoContainer.background = "black";
    videoContainer.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    videoContainer.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
    videoContainer.left = "40px";
    videoContainer.top = "40px";
    videoContainer.shadowBlur = 20;
    videoContainer.shadowColor = "rgba(0,0,0,0.9)";
    this._container.addControl(videoContainer);

    // Create a native HTML video element for guaranteed playback
    this._videoElement = document.createElement("video");
    this._videoElement.src = "assets/menu_clip.mp4";
    this._videoElement.muted = true;
    this._videoElement.loop = true;
    this._videoElement.autoplay = true;
    this._videoElement.playsInline = true;
    this._videoElement.style.position = "absolute";
    this._videoElement.style.borderRadius = "14px";
    this._videoElement.style.objectFit = "cover";
    this._videoElement.style.zIndex = "100";
    this._videoElement.style.pointerEvents = "none";
    this._videoElement.style.display = "block";
    this._videoElement.style.border = "none";
    document.body.appendChild(this._videoElement);

    // Dynamic Positioning Logic
    const syncVideoPosition = () => {
      const canvas = scene.getEngine().getRenderingCanvas();
      if (!canvas || !this._videoElement || !this._container.isVisible) {
        if (this._videoElement) this._videoElement.style.display = "none";
        return;
      }
      
      this._videoElement.style.display = "block";
      const canvasRect = canvas.getBoundingClientRect();
      const adt = this._texture;
      const adtWidth = adt.getSize().width;
      const adtHeight = adt.getSize().height;
      
      if (adtWidth === 0 || adtHeight === 0) return;

      const ratioX = canvasRect.width / adtWidth;
      const ratioY = canvasRect.height / adtHeight;
      
      const guiX = 40;
      const guiY = 40;
      const guiW = 450;
      const guiH = 450;
      
      const padding = 8 * ratioX;
      this._videoElement.style.left = `${canvasRect.left + (guiX * ratioX) + padding}px`;
      this._videoElement.style.top = `${canvasRect.top + (guiY * ratioY) + padding}px`;
      this._videoElement.style.width = `${(guiW * ratioX) - (padding * 2)}px`;
      this._videoElement.style.height = `${(guiH * ratioY) - (padding * 2)}px`;
    };

    // Sync on every frame and on resize
    const positionObserver = scene.onBeforeRenderObservable.add(syncVideoPosition);
    window.addEventListener("resize", syncVideoPosition);

    // Cleanup logic
    this._texture.onDisposeObservable.add(() => {
        scene.onBeforeRenderObservable.remove(positionObserver);
        window.removeEventListener("resize", syncVideoPosition);
    });

    // Title
    const title = new GUI.TextBlock("title", "⚽ AMN FOOTBALL");
    title.color = "#00ff88";
    title.fontSize = 72;
    title.fontWeight = "900";
    title.fontFamily = "Orbitron, sans-serif";
    title.outlineWidth = 4;
    title.outlineColor = "black";
    title.top = "-140px";
    this._container.addControl(title);

    // Subtitle
    const subtitle = new GUI.TextBlock(
      "subtitle",
      "WASD MOVE • SHIFT SPRINT • SPACE SHOOT • E THROUGH • Q LOB • F TACKLE"
    );
    subtitle.color = "white";
    subtitle.fontSize = 14;
    subtitle.fontFamily = "Orbitron, sans-serif";
    subtitle.top = "-60px";
    subtitle.alpha = 0.8;
    this._container.addControl(subtitle);

    // Practice Mode button
    const practiceBtn = new GUI.Rectangle("practiceBtn");
    practiceBtn.width = "280px";
    practiceBtn.height = "55px";
    practiceBtn.cornerRadius = 4;
    practiceBtn.thickness = 1;
    practiceBtn.color = "white";
    practiceBtn.background = "rgba(34, 136, 34, 0.8)";
    practiceBtn.top = "20px";
    practiceBtn.isHitTestVisible = true;
    practiceBtn.isPointerBlocker = true;
    this._container.addControl(practiceBtn);

    const practiceText = new GUI.TextBlock("practiceText", "PRACTICE SESSION");
    practiceText.color = "white";
    practiceText.fontSize = 18;
    practiceText.fontWeight = "700";
    practiceText.fontFamily = "Orbitron, sans-serif";
    practiceBtn.addControl(practiceText);

    practiceBtn.onPointerDownObservable.add(() => {
      practiceBtn.background = "rgba(26, 107, 26, 0.9)";
      practiceBtn.scaleX = 0.98;
      practiceBtn.scaleY = 0.98;
    });
    practiceBtn.onPointerUpObservable.add(() => {
      practiceBtn.background = "rgba(34, 136, 34, 0.8)";
      practiceBtn.scaleX = 1;
      practiceBtn.scaleY = 1;
      onPractice();
    });
    practiceBtn.onPointerEnterObservable.add(() => {
      practiceBtn.thickness = 3;
      practiceBtn.background = "rgba(40, 160, 40, 1)";
    });
    practiceBtn.onPointerOutObservable.add(() => {
      practiceBtn.thickness = 1;
      practiceBtn.background = "rgba(34, 136, 34, 0.8)";
    });

    // Practice label
    const practiceLabel = new GUI.TextBlock("practiceLabel", "SOLO DRILLS & SHOT PRACTICE");
    practiceLabel.color = "#88cc88";
    practiceLabel.fontSize = 11;
    practiceLabel.fontFamily = "Inter, sans-serif";
    practiceLabel.top = "65px";
    practiceLabel.height = "20px";
    this._container.addControl(practiceLabel);

    // Play Match button
    const playBtn = new GUI.Rectangle("playBtn");
    playBtn.width = "280px";
    playBtn.height = "65px";
    playBtn.cornerRadius = 4;
    playBtn.thickness = 1;
    playBtn.color = "#00ff88";
    playBtn.background = "rgba(17, 85, 204, 0.8)";
    playBtn.top = "130px";
    playBtn.isHitTestVisible = true;
    playBtn.isPointerBlocker = true;
    this._container.addControl(playBtn);

    const playText = new GUI.TextBlock("playText", "START MATCH");
    playText.color = "white";
    playText.fontSize = 22;
    playText.fontWeight = "700";
    playText.fontFamily = "Orbitron, sans-serif";
    playBtn.addControl(playText);

    playBtn.onPointerDownObservable.add(() => {
      playBtn.background = "rgba(13, 68, 153, 0.9)";
      playBtn.scaleX = 0.98;
      playBtn.scaleY = 0.98;
    });
    playBtn.onPointerUpObservable.add(() => {
      playBtn.background = "rgba(17, 85, 204, 0.8)";
      playBtn.scaleX = 1;
      playBtn.scaleY = 1;
      if (this._onPlayWithDifficulty) {
        this._onPlayWithDifficulty(this._selectedDifficulty);
      } else {
        onPlay();
      }
    });
    playBtn.onPointerEnterObservable.add(() => {
      playBtn.thickness = 3;
      playBtn.background = "rgba(25, 100, 230, 1)";
    });
    playBtn.onPointerOutObservable.add(() => {
      playBtn.thickness = 1;
      playBtn.background = "rgba(17, 85, 204, 0.8)";
    });

    // Difficulty Selection Section
    const difficultyLabel = new GUI.TextBlock("difficultyLabel", "MATCH LEVEL");
    difficultyLabel.color = "#aaaaaa";
    difficultyLabel.fontSize = 12;
    difficultyLabel.fontWeight = "700";
    difficultyLabel.fontFamily = "Orbitron, sans-serif";
    difficultyLabel.top = "195px";
    difficultyLabel.height = "20px";
    this._container.addControl(difficultyLabel);

    // Difficulty buttons container - first row
    const difficultyPanel1 = new GUI.StackPanel("difficultyPanel1");
    difficultyPanel1.isVertical = false;
    difficultyPanel1.height = "45px";
    difficultyPanel1.top = "230px";
    this._container.addControl(difficultyPanel1);

    // Easy difficulty button
    this._easyBtn = new GUI.Rectangle("easyBtn");
    this._easyBtn.width = "100px";
    this._easyBtn.height = "35px";
    this._easyBtn.cornerRadius = 2;
    this._easyBtn.thickness = 2;
    this._easyBtn.color = "#44cc44";
    this._easyBtn.background = "rgba(26, 90, 26, 0.6)";
    this._easyBtn.isHitTestVisible = true;
    this._easyBtn.isPointerBlocker = true;
    difficultyPanel1.addControl(this._easyBtn);

    const easyText = new GUI.TextBlock("easyText", "EASY");
    easyText.color = "white";
    easyText.fontSize = 12;
    easyText.fontWeight = "700";
    easyText.fontFamily = "Orbitron, sans-serif";
    this._easyBtn.addControl(easyText);

    this._easyBtn.onPointerUpObservable.add(() => {
      this._setDifficulty(DifficultyLevel.EASY);
    });

    // Spacer
    const spacer1 = new GUI.Rectangle("spacer1");
    spacer1.width = "10px";
    spacer1.thickness = 0;
    difficultyPanel1.addControl(spacer1);

    // Medium difficulty button
    this._mediumBtn = new GUI.Rectangle("mediumBtn");
    this._mediumBtn.width = "100px";
    this._mediumBtn.height = "35px";
    this._mediumBtn.cornerRadius = 2;
    this._mediumBtn.thickness = 1;
    this._mediumBtn.color = "#666666";
    this._mediumBtn.background = "rgba(30, 30, 30, 0.6)";
    this._mediumBtn.isHitTestVisible = true;
    this._mediumBtn.isPointerBlocker = true;
    difficultyPanel1.addControl(this._mediumBtn);

    const mediumText = new GUI.TextBlock("mediumText", "MEDIUM");
    mediumText.color = "#aaaaaa";
    mediumText.fontSize = 12;
    mediumText.fontWeight = "700";
    mediumText.fontFamily = "Orbitron, sans-serif";
    this._mediumBtn.addControl(mediumText);

    this._mediumBtn.onPointerUpObservable.add(() => {
      this._setDifficulty(DifficultyLevel.MEDIUM);
    });

    // Difficulty buttons container - second row
    const difficultyPanel2 = new GUI.StackPanel("difficultyPanel2");
    difficultyPanel2.isVertical = false;
    difficultyPanel2.height = "45px";
    difficultyPanel2.top = "270px";
    this._container.addControl(difficultyPanel2);

    // Hard difficulty button
    this._hardBtn = new GUI.Rectangle("hardBtn");
    this._hardBtn.width = "100px";
    this._hardBtn.height = "35px";
    this._hardBtn.cornerRadius = 2;
    this._hardBtn.thickness = 1;
    this._hardBtn.color = "#666666";
    this._hardBtn.background = "rgba(30, 30, 30, 0.6)";
    this._hardBtn.isHitTestVisible = true;
    this._hardBtn.isPointerBlocker = true;
    difficultyPanel2.addControl(this._hardBtn);

    const hardText = new GUI.TextBlock("hardText", "HARD");
    hardText.color = "#aaaaaa";
    hardText.fontSize = 12;
    hardText.fontWeight = "700";
    hardText.fontFamily = "Orbitron, sans-serif";
    this._hardBtn.addControl(hardText);

    this._hardBtn.onPointerUpObservable.add(() => {
      this._setDifficulty(DifficultyLevel.HARD);
    });

    // Spacer
    const spacer2 = new GUI.Rectangle("spacer2");
    spacer2.width = "10px";
    spacer2.thickness = 0;
    difficultyPanel2.addControl(spacer2);

    // Impossible difficulty button
    this._impossibleBtn = new GUI.Rectangle("impossibleBtn");
    this._impossibleBtn.width = "100px";
    this._impossibleBtn.height = "35px";
    this._impossibleBtn.cornerRadius = 2;
    this._impossibleBtn.thickness = 1;
    this._impossibleBtn.color = "#666666";
    this._impossibleBtn.background = "rgba(30, 30, 30, 0.6)";
    this._impossibleBtn.isHitTestVisible = true;
    this._impossibleBtn.isPointerBlocker = true;
    difficultyPanel2.addControl(this._impossibleBtn);

    const impossibleText = new GUI.TextBlock("impossibleText", "ELITE");
    impossibleText.color = "#aaaaaa";
    impossibleText.fontSize = 12;
    impossibleText.fontWeight = "700";
    impossibleText.fontFamily = "Orbitron, sans-serif";
    this._impossibleBtn.addControl(impossibleText);

    this._impossibleBtn.onPointerUpObservable.add(() => {
      this._setDifficulty(DifficultyLevel.IMPOSSIBLE);
    });

    // Difficulty description
    this._difficultyDesc = new GUI.TextBlock("difficultyDesc", "BASIC OPPONENT CHALLENGE");
    this._difficultyDesc.color = "#888888";
    this._difficultyDesc.fontSize = 11;
    this._difficultyDesc.fontFamily = "Inter, sans-serif";
    this._difficultyDesc.top = "310px";
    this._difficultyDesc.height = "20px";
    this._container.addControl(this._difficultyDesc);

    // --- Settings Section ---
    const settingsPanel = new GUI.StackPanel("settingsPanel");
    settingsPanel.width = "300px";
    settingsPanel.height = "100px";
    settingsPanel.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
    settingsPanel.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    settingsPanel.left = "-20px";
    settingsPanel.top = "-20px";
    settingsPanel.isPointerBlocker = true;
    this._container.addControl(settingsPanel);

    const crowdTogglePanel = new GUI.StackPanel("crowdTogglePanel");
    crowdTogglePanel.isVertical = false;
    crowdTogglePanel.height = "40px";
    settingsPanel.addControl(crowdTogglePanel);

    const checkbox = new GUI.Checkbox();
    checkbox.width = "18px";
    checkbox.height = "18px";
    checkbox.isChecked = true;
    checkbox.color = "#00ff88";
    checkbox.onIsCheckedChangedObservable.add((value) => {
      onCrowdToggle(value);
    });
    crowdTogglePanel.addControl(checkbox);

    const checkboxText = new GUI.TextBlock();
    checkboxText.text = " HIGH PERFORMANCE CROWD";
    checkboxText.color = "white";
    checkboxText.fontSize = 12;
    checkboxText.fontFamily = "Orbitron, sans-serif";
    checkboxText.width = "250px";
    checkboxText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    crowdTogglePanel.addControl(checkboxText);
  }

  /**
   * Set the selected difficulty and update UI
   */
  private _setDifficulty(level: DifficultyLevel): void {
    this._selectedDifficulty = level;
    console.log(`Difficulty selected: ${level}`);

    // Reset all buttons to unselected state
    const resetBtn = (btn: GUI.Rectangle | undefined) => {
      if (btn) {
        btn.thickness = 1;
        btn.color = "#666666";
        btn.background = "rgba(30, 30, 30, 0.6)";
        const text = btn.children[0] as GUI.TextBlock;
        if (text) text.color = "#aaaaaa";
      }
    };

    resetBtn(this._easyBtn);
    resetBtn(this._mediumBtn);
    resetBtn(this._hardBtn);
    resetBtn(this._impossibleBtn);

    // Set selected button style and description
    if (this._difficultyDesc) {
      let selectedBtn: GUI.Rectangle | undefined;
      let color: string = "#ffffff";
      let bgColor: string = "rgba(100, 100, 100, 0.8)";
      
      switch (level) {
        case DifficultyLevel.EASY:
          selectedBtn = this._easyBtn;
          color = "#44cc44";
          bgColor = "rgba(26, 90, 26, 0.7)";
          this._difficultyDesc.text = "ROOKIE: 1 BOT - FREE ROAM & CHASE";
          break;
        case DifficultyLevel.MEDIUM:
          selectedBtn = this._mediumBtn;
          color = "#cc8844";
          bgColor = "rgba(90, 58, 26, 0.7)";
          this._difficultyDesc.text = "AMATEUR: 2 BOTS - ZONE DEFENSE";
          break;
        case DifficultyLevel.HARD:
          selectedBtn = this._hardBtn;
          color = "#cc4444";
          bgColor = "rgba(90, 26, 26, 0.7)";
          this._difficultyDesc.text = "PRO: 3 BOTS - COORDINATED ATTACK";
          break;
        case DifficultyLevel.IMPOSSIBLE:
          selectedBtn = this._impossibleBtn;
          color = "#aa44cc";
          bgColor = "rgba(74, 26, 90, 0.7)";
          this._difficultyDesc.text = "ELITE: 11 BOTS - FULL 4-3-3 FORMATION";
          break;
      }

      if (selectedBtn) {
        selectedBtn.thickness = 2;
        selectedBtn.color = color;
        selectedBtn.background = bgColor;
        const text = selectedBtn.children[0] as GUI.TextBlock;
        if (text) text.color = "white";
      }
    }
  }

  setVisible(visible: boolean): void {
    this._container.isVisible = visible;
    this._container.isHitTestVisible = visible;
    
    // Play/Pause and Show/Hide HTML video when menu is shown/hidden
    if (this._videoElement) {
        if (visible) {
            this._videoElement.style.display = "block";
            this._videoElement.play().catch(() => {});
        } else {
            this._videoElement.style.display = "none";
            this._videoElement.pause();
        }
    }
  }

  dispose(): void {
    this._texture.dispose();
    if (this._videoElement) {
        this._videoElement.remove();
    }
  }
}
